import React, { useEffect, useRef, useState } from "react";
import { Check, Loader2, Info, XCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams, useNavigate } from "react-router-dom";
import { extrasApi, bl } from "@/lib/extrasApi";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 15; // 30s total

export default function Billing() {
    const { locale } = useI18n();
    const { user, refresh } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [plan, setPlan] = useState(null);
    const [packs, setPacks] = useState([]);
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [buying, setBuying] = useState(null); // pack_id being processed
    const [payStatus, setPayStatus] = useState(null); // { kind, message }
    const pollRef = useRef(null);

    // Load billing overview
    useEffect(() => {
        Promise.all([extrasApi.billing.plan(), extrasApi.billing.packs(), extrasApi.billing.invoices()])
            .then(([p, pk, inv]) => { setPlan(p); setPacks(pk); setInvoices(inv); })
            .catch(() => toast.error("Failed"))
            .finally(() => setLoading(false));
    }, []);

    // Handle post-checkout return
    useEffect(() => {
        if (searchParams.get("cancelled") === "1") {
            setPayStatus({ kind: "cancelled", message: bl("Payment cancelled.", "Paiement annulé.", locale) });
            const p = new URLSearchParams(searchParams); p.delete("cancelled"); setSearchParams(p, { replace: true });
            return;
        }
        const sessionId = searchParams.get("session_id");
        if (searchParams.get("success") === "1" && sessionId) {
            setPayStatus({ kind: "polling", message: bl("Confirming your payment…", "Confirmation du paiement…", locale) });
            let attempts = 0;
            const poll = async () => {
                attempts += 1;
                try {
                    const s = await extrasApi.billing.status(sessionId);
                    if (s.payment_status === "paid") {
                        setPayStatus({ kind: "success", message: bl(`+${s.credits} credits added to your account.`, `+${s.credits} crédits ajoutés à ton compte.`, locale) });
                        await refresh();
                        const p = new URLSearchParams(searchParams); p.delete("success"); p.delete("session_id"); setSearchParams(p, { replace: true });
                        return;
                    }
                    if (s.payment_status === "expired" || s.payment_status === "failed") {
                        setPayStatus({ kind: "error", message: bl("Payment did not complete.", "Le paiement n'a pas abouti.", locale) });
                        return;
                    }
                    if (attempts >= POLL_MAX_ATTEMPTS) {
                        setPayStatus({ kind: "error", message: bl("Payment is still processing. Refresh in a minute.", "Paiement en cours de traitement. Rafraîchis dans une minute.", locale) });
                        return;
                    }
                    pollRef.current = setTimeout(poll, POLL_INTERVAL_MS);
                } catch (_e) {
                    setPayStatus({ kind: "error", message: bl("Could not verify payment.", "Impossible de vérifier le paiement.", locale) });
                }
            };
            poll();
        }
        return () => { if (pollRef.current) clearTimeout(pollRef.current); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function buy(pack) {
        if (!plan?.stripe_configured) {
            toast.info(bl("Stripe not configured yet.", "Stripe pas encore configuré.", locale));
            return;
        }
        setBuying(pack.pack_id);
        try {
            const res = await extrasApi.billing.checkout(pack.pack_id);
            window.location.href = res.checkout_url;
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to start checkout");
            setBuying(null);
        }
    }

    if (loading) return <div className="p-10"><Loader2 className="animate-spin text-zinc-500" /></div>;

    return (
        <div className="px-6 lg:px-10 py-6">
            <div className="mb-6">
                <h1 className="font-display font-black text-3xl tracking-tighter">{bl("Credits & Billing", "Crédits & Facturation", locale)}</h1>
                <p className="mt-1 text-sm text-zinc-500">{bl("Buy credit packs, download invoices.", "Achète des packs, télécharge tes factures.", locale)}</p>
            </div>

            {payStatus && (
                <div
                    data-testid="billing-payment-status"
                    className={`mb-6 rounded-xl p-4 border text-sm flex gap-2 items-start ${
                        payStatus.kind === "success" ? "bg-emerald-500/10 border-emerald-500/25 text-emerald-200" :
                        payStatus.kind === "error" ? "bg-rose-500/10 border-rose-500/25 text-rose-200" :
                        payStatus.kind === "cancelled" ? "bg-amber-500/10 border-amber-500/25 text-amber-200" :
                        "bg-brand-cyan/10 border-brand-cyan/25 text-brand-cyan"
                    }`}
                >
                    {payStatus.kind === "success" ? <CheckCircle2 size={16} className="shrink-0 mt-0.5" /> :
                     payStatus.kind === "polling" ? <Loader2 size={16} className="shrink-0 mt-0.5 animate-spin" /> :
                     <XCircle size={16} className="shrink-0 mt-0.5" />}
                    <div>{payStatus.message}</div>
                </div>
            )}

            {!plan?.stripe_configured && (
                <div className="mb-6 rounded-xl p-4 bg-amber-500/10 border border-amber-500/25 text-sm text-amber-200 flex gap-2" data-testid="billing-simulation-banner">
                    <Info size={16} className="shrink-0 mt-0.5" />
                    <div>{bl("Stripe integration is not configured on the server.", "Intégration Stripe non configurée côté serveur.", locale)}</div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">{bl("Current plan", "Plan actuel", locale)}</div>
                    <div className="mt-2 font-display font-black text-3xl tracking-tighter capitalize">{plan?.plan}</div>
                </div>
                <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">{bl("Credits", "Crédits", locale)}</div>
                    <div className="mt-2 font-display font-black text-3xl tracking-tighter" data-testid="billing-credits">{user?.credits ?? 0}</div>
                </div>
                <div className="rounded-2xl p-6 bg-brand-surface border border-white/8">
                    <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold">Stripe</div>
                    <div className="mt-2 font-display font-black text-2xl tracking-tighter inline-flex items-center gap-2">
                        {plan?.stripe_configured ? <><Check size={20} className="text-emerald-400" /> Live</> : <>⏳ Pending</>}
                    </div>
                </div>
            </div>

            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">{bl("Credit packs", "Packs de crédits", locale)}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {packs.map(p => (
                    <div key={p.pack_id} className={`relative rounded-2xl p-5 border ${p.highlight ? "bg-gradient-to-b from-brand-indigo/20 to-brand-surface border-brand-indigo/40" : "bg-brand-surface border-white/8"}`} data-testid={`pack-${p.pack_id}`}>
                        {p.highlight && <span className="absolute -top-3 left-4 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest bg-brand-cyan text-brand-void">Best value</span>}
                        <div className="text-sm text-zinc-300">{p.label}</div>
                        <div className="mt-2 font-display font-black text-3xl tracking-tighter">{p.price_eur}€</div>
                        <div className="text-xs text-zinc-500 font-mono">{p.credits.toLocaleString()} credits</div>
                        {p.savings_pct > 0 && <div className="mt-2 text-[11px] font-mono text-brand-cyan">-{p.savings_pct}% vs pack starter</div>}
                        <button
                            onClick={() => buy(p)}
                            disabled={buying !== null}
                            data-testid={`pack-buy-${p.pack_id}`}
                            className={`mt-4 w-full text-sm font-semibold py-2 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-60 ${p.highlight ? "btn-cta text-white" : "bg-white/5 hover:bg-white/10 text-white border border-white/10"}`}
                        >
                            {buying === p.pack_id ? <Loader2 size={13} className="animate-spin" /> : null}
                            {buying === p.pack_id ? bl("Redirecting…", "Redirection…", locale) : bl("Buy pack", "Acheter", locale)}
                        </button>
                    </div>
                ))}
            </div>

            <div className="text-xs uppercase tracking-widest font-bold text-zinc-500 mb-3">{bl("Invoices", "Factures", locale)}</div>
            <div className="rounded-2xl bg-brand-surface border border-white/8 p-6 text-sm text-zinc-500 text-center">
                {invoices.length === 0 ? bl("No invoices yet.", "Aucune facture pour l'instant.", locale) : `${invoices.length} invoices`}
            </div>

            <p className="mt-6 text-[11px] text-zinc-600 font-mono">
                {bl("Payments processed securely by Stripe. VAT invoice available in your Stripe receipt email.",
                    "Paiements sécurisés par Stripe. Facture TVA disponible dans le reçu Stripe envoyé par email.", locale)}
            </p>
        </div>
    );
}
